import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Divider,
    Paper,
    Slider,
    TextField,
    Typography,
} from '@mui/material';
import { useAuth } from '../components/AuthContext';
import {
    createProjectRequest,
    fetchCompanyTopics,
    searchCompanies,
} from '../services/credits';
import {
    REVIEW_CAP_STOPS,
    formatReviewCount,
    formatCredits,
    formatNumber,
    quotedCredits,
} from '../components/Credits/creditFormula';
import ProjectTopicEditor from '../components/Credits/ProjectTopicEditor';
import RequestTopicDialog from '../components/Credits/RequestTopicDialog';

const emptyTopicConfig = () => ({
    canned_assessments: [],
    custom_questions: [],
    competitor_topic_ids: [],
});

function NewProjectRequest() {
    const { userDetails } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const orgId = userDetails?.user_org?.id;
    const returnTo = location.state?.from || '/home';

    const [name, setName] = useState('');
    const [capIndex, setCapIndex] = useState(2);
    const reviewCap = REVIEW_CAP_STOPS[capIndex];

    const [companyQuery, setCompanyQuery] = useState('');
    const [companyOptions, setCompanyOptions] = useState([]);
    const [companySearching, setCompanySearching] = useState(false);
    const [company, setCompany] = useState(null);

    const [companyTopics, setCompanyTopics] = useState([]);
    const [topicsLoading, setTopicsLoading] = useState(false);
    const [selectedTopics, setSelectedTopics] = useState([]); // [{ topic, config }]
    const [requestingTopic, setRequestingTopic] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Debounced company search.
    useEffect(() => {
        if (!companyQuery || companyQuery.trim().length < 2) {
            setCompanyOptions([]);
            return;
        }
        const handle = setTimeout(async () => {
            setCompanySearching(true);
            try {
                const result = await searchCompanies(companyQuery.trim());
                setCompanyOptions(result.companies || result || []);
            } catch (e) {
                setError(e.message);
            } finally {
                setCompanySearching(false);
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [companyQuery]);

    // Load topics whenever company changes.
    useEffect(() => {
        if (!company) {
            setCompanyTopics([]);
            setSelectedTopics([]);
            return;
        }
        let cancelled = false;
        setTopicsLoading(true);
        fetchCompanyTopics(company.id)
            .then(data => {
                if (cancelled) return;
                setCompanyTopics(data.topics || data || []);
                setSelectedTopics([]);
            })
            .catch(e => { if (!cancelled) setError(e.message); })
            .finally(() => { if (!cancelled) setTopicsLoading(false); });
        return () => { cancelled = true; };
    }, [company]);

    const availableTopics = useMemo(
        () => companyTopics.filter(t =>
            !selectedTopics.some(st => st.topic.id === t.id),
        ),
        [companyTopics, selectedTopics],
    );

    const addTopic = (topic) => {
        if (!topic) return;
        setSelectedTopics(prev => [...prev, { topic, config: emptyTopicConfig() }]);
    };

    const updateTopicConfig = (topicId, nextConfig) => {
        setSelectedTopics(prev => prev.map(st =>
            st.topic.id === topicId ? { ...st, config: nextConfig } : st,
        ));
    };

    const removeTopic = (topicId) => {
        setSelectedTopics(prev => prev.filter(st => st.topic.id !== topicId));
    };

    const onTopicRequested = (newTopic) => {
        setRequestingTopic(false);
        // Pending topic still attaches to the project.
        setCompanyTopics(prev => [...prev, newTopic]);
        addTopic(newTopic);
    };

    const credits = useMemo(() => {
        const topicsForCalc = selectedTopics.map(st => ({
            competitorCount: st.config.competitor_topic_ids.length,
            customQuestionCount: st.config.custom_questions.length,
        }));
        return quotedCredits(reviewCap, topicsForCalc);
    }, [reviewCap, selectedTopics]);

    const submit = async () => {
        setError(null);
        if (!name.trim()) { setError('Project name is required.'); return; }
        if (!orgId) { setError('No organization on file for this user.'); return; }
        if (!company) { setError('Pick a company.'); return; }
        if (selectedTopics.length === 0) { setError('Add at least one topic.'); return; }
        setSubmitting(true);
        try {
            await createProjectRequest({
                name: name.trim(),
                companyId: company.id,
                reviewCap,
                topics: selectedTopics.map(st => ({
                    topicId: st.topic.id,
                    cannedAssessments: st.config.canned_assessments,
                    customQuestions: st.config.custom_questions,
                    competitorTopicIds: st.config.competitor_topic_ids,
                })),
            });
            navigate(returnTo);
        } catch (e) {
            setError(e.message);
            setSubmitting(false);
        }
    };

    const sliderMarks = REVIEW_CAP_STOPS.map((value, idx) => ({
        value: idx, label: formatReviewCount(value),
    }));

    return (
        <div className="centered-container">
            <div className="standard-width">
                <Box sx={{ py: 3 }}>
                    <Typography variant="h4" sx={{ mb: 1 }}>New project request</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Pick a company, add topics, configure each. You'll be charged based
                        on actual usage, capped at the quoted credits below.
                    </Typography>

                    <Paper sx={{ p: 3 }}>
                        <TextField
                            fullWidth
                            label="Project name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            sx={{ mb: 3 }}
                        />

                        <Autocomplete
                            value={company}
                            onChange={(_, val) => setCompany(val)}
                            inputValue={companyQuery}
                            onInputChange={(_, val) => setCompanyQuery(val)}
                            options={companyOptions}
                            getOptionLabel={o => o?.name || ''}
                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                            loading={companySearching}
                            filterOptions={x => x}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Company"
                                    placeholder="Search by name…"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {companySearching ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            sx={{ mb: 1 }}
                        />
                        <Box sx={{ mb: 3 }}>
                            <Button size="small" onClick={() => navigate('/companies/new', { state: { from: location.pathname } })}>
                                Don't see it? Add a new company
                            </Button>
                        </Box>

                        {company && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" sx={{ mb: 1 }}>Topics</Typography>
                                {topicsLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <>
                                        <Autocomplete
                                            value={null}
                                            onChange={(_, val) => addTopic(val)}
                                            options={availableTopics}
                                            getOptionLabel={o => o?.topic || ''}
                                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                            renderInput={params => (
                                                <TextField {...params} label="Add a topic" placeholder="Pick from this company's topics…" />
                                            )}
                                            sx={{ mb: 1 }}
                                        />
                                        <Box sx={{ mb: 3 }}>
                                            <Button size="small" onClick={() => setRequestingTopic(true)}>
                                                Don't see it? Request a topic
                                            </Button>
                                        </Box>

                                        {selectedTopics.map(st => (
                                            <ProjectTopicEditor
                                                key={st.topic.id}
                                                topic={st.topic}
                                                config={st.config}
                                                onChange={cfg => updateTopicConfig(st.topic.id, cfg)}
                                                onRemove={() => removeTopic(st.topic.id)}
                                            />
                                        ))}
                                    </>
                                )}
                            </>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Typography gutterBottom>
                            Review cap: <strong>{formatNumber(reviewCap)}</strong>
                        </Typography>
                        <Box sx={{ px: 1, mb: 3 }}>
                            <Slider
                                value={capIndex}
                                onChange={(_, v) => setCapIndex(v)}
                                min={0}
                                max={REVIEW_CAP_STOPS.length - 1}
                                step={null}
                                marks={sliderMarks}
                                valueLabelDisplay="off"
                            />
                        </Box>

                        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                            <Typography variant="overline" color="text.secondary">Quoted credits</Typography>
                            <Typography variant="h5">{formatCredits(credits)}</Typography>
                        </Paper>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button variant="contained" onClick={submit} disabled={submitting}>
                                {submitting ? 'Submitting…' : 'Submit request'}
                            </Button>
                            <Button onClick={() => navigate(returnTo)} disabled={submitting}>
                                Cancel
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </div>

            <RequestTopicDialog
                open={requestingTopic}
                companyId={company?.id}
                onClose={() => setRequestingTopic(false)}
                onCreated={onTopicRequested}
            />
        </div>
    );
}

export default NewProjectRequest;
